import React, { useState } from 'react';
import { Button, Modal, Form, Card, Collapse } from 'react-bootstrap';
import { CreditCard, CheckCircle, Trash2, Info, Calendar, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import InfoHint from '../../../SharedComponents/InfoHint';
import moment from 'moment';

// Skatteverket's OCR e-service. F-9, resolved at Gate A 2026-09-02: Skatteverket
// publishes NO OCR algorithm, only this calculator.
// Canonical target; the Gate A citation (…/foretag/…/ocrberakning.106.…) 301s here.
const OCR_SERVICE_URL = 'https://www.skatteverket.se/privat/etjansterochblanketter/allaetjanster/oinloggadetjanster/ocrberakning.4.18e1b10334ebe8bc80005111.html';

const PaymentStep = ({
    totalVat,
    paymentMadeDate,
    handlePaymentMade,
    handleClearPayment,
    vatPeriod,
    completionStatus,
    operationInProgress,
    isPreviewMode,
}) => {
    // Use the stored vat_total from the period (set during XML generation)
    // The live totalVat may be zero after the zeroing verification
    const declaredVat = vatPeriod?.vat_total || totalVat || 0;
    const [showDateModal, setShowDateModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(moment().format('YYYY-MM-DD'));
    const [showInstructions, setShowInstructions] = useState(false);

    const handleSubmitPaymentDate = () => {
        handlePaymentMade(selectedDate);
        setShowDateModal(false);
    };

    return (
        <div className="step-container">
            <style>{`
                .payment-info-card {
                    margin-bottom: 1rem;
                }

                .amount-display {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 1rem;
                    margin-bottom: 1rem;
                }

                .ocr-display {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    font-family: 'Courier New', monospace;
                    font-size: 1.125rem;
                    font-weight: 600;
                    letter-spacing: 0.05em;
                    color: #1e293b;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .copy-button {
                    background: var(--primary);
                    border: none;
                    color: white;
                    padding: 0.5rem 0.75rem;
                    border-radius: 6px;
                    transition: all 0.2s;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 0.5rem;
                }

                .copy-button:hover {
                    background: var(--primary-hover);
                    transform: translateY(-1px);
                    box-shadow: 0 2px 4px rgba(59, 130, 246, 0.3);
                }

                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    margin-bottom: 0.75rem;
                }

                .toggle-instructions-btn {
                    background: white;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    padding: 0.75rem 1rem;
                    width: 100%;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    cursor: pointer;
                    transition: all 0.2s;
                    margin-bottom: 1.5rem;
                }

                .toggle-instructions-btn:hover {
                    background: #f8fafc;
                    border-color: #cbd5e1;
                }

                .instructions-card {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 10px;
                    padding: 1.25rem;
                    margin-bottom: 1.5rem;
                }

                .instruction-step {
                    display: flex;
                    align-items: start;
                    margin-bottom: 1rem;
                    padding: 0.75rem;
                    background: white;
                    border-radius: 8px;
                    border: 1px solid #e2e8f0;
                }

                .instruction-step:last-child {
                    margin-bottom: 0;
                }

                .step-number {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    background: var(--primary);
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 600;
                    font-size: 0.875rem;
                    margin-right: 1rem;
                }

                /* Was a 2px border in var(--bs-success) — #68e365, a neon green
                   nowhere near the app's --primary #44814E. Matches the
                   submission step's confirmation panel instead. */
                .payment-complete-card {
                    background: var(--rgba-primary-1, #f4f8f5);
                    border: 1px solid var(--rgba-primary-3, #cfe3d3);
                    border-radius: 10px;
                    padding: 1.25rem 1.35rem;
                }
            `}</style>

            <div className="step-header">
                <div className="d-flex align-items-center">
                    <div
                        className="rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: 'var(--primary)',
                            color: 'white'
                        }}
                    >
                        <CreditCard size={24} />
                    </div>
                    <div>
                        <h3 className="mb-1">Betala Momsen</h3>
                        <p className="text-muted mb-0">Betala momsen till Skatteverket</p>
                    </div>
                </div>
            </div>

            <div className="step-content">
                {paymentMadeDate ? (
                    <div className="payment-complete-card">
                        <div className="d-flex align-items-start justify-content-between">
                            <div className="d-flex align-items-start">
                                <CheckCircle
                                    size={22}
                                    className="me-3 mt-1"
                                    style={{ flexShrink: 0, color: 'var(--primary)' }}
                                />
                                <div>
                                    <h5 className="mb-2 fw-semibold" style={{ color: 'var(--primary)', fontSize: '1rem' }}>
                                        Betalning registrerad
                                    </h5>
                                    <div className="d-flex align-items-center mb-2 text-muted">
                                        <Calendar size={16} className="me-2" />
                                        <span>{moment(paymentMadeDate).format('D MMMM YYYY')}</span>
                                    </div>
                                    <div className="text-muted" style={{ fontSize: '0.875rem' }}>
                                        Betalningen av {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(declaredVat)} har registrerats.
                                    </div>
                                </div>
                            </div>
                            {vatPeriod?.state === 'closed' && !completionStatus?.record_completed && (
                                <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={handleClearPayment}
                                    disabled={operationInProgress || isPreviewMode}
                                    className="d-inline-flex align-items-center"
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    <Trash2 size={14} className="me-1" />
                                    Ångra
                                </Button>
                            )}
                        </div>
                    </div>
                ) : (
                    <>
                        <div className="payment-info-card">
                            <div className="d-flex align-items-center mb-3">
                                <CreditCard size={20} className="text-primary me-2" />
                                <h5 className="mb-0 fw-semibold">
                                    {declaredVat < 0 ? 'Moms att få tillbaka' : 'Betalningsinformation'}
                                </h5>
                            </div>

                            {declaredVat < 0 ? (
                                <div className="amount-display mb-3">
                                    <div className="text-muted small mb-2">Belopp att få tillbaka från Skatteverket</div>
                                    <div style={{ fontSize: '1.75rem', fontWeight: '700', color: 'var(--primary)' }}>
                                        {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(Math.abs(declaredVat))}
                                    </div>
                                    <div className="text-muted small mt-2">
                                        Skatteverket betalar tillbaka beloppet till ditt skattekonto. Ingen betalning behövs.
                                    </div>
                                </div>
                            ) : (
                            <div className="amount-display mb-3">
                                <div className="text-muted small mb-2">Belopp att betala</div>
                                <div className="text-dark" style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                                    {new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(declaredVat)}
                                </div>
                            </div>
                            )}

                            {declaredVat > 0 && (
                                <>
                                    <div className="info-row mb-2">
                                        <div className="text-muted small">Mottagare</div>
                                        <div className="fw-medium">Skatteverket</div>
                                    </div>

                                    <div className="info-row mb-2">
                                        <div className="text-muted small">Bankgiro</div>
                                        <div className="fw-medium">5050-1055</div>
                                    </div>

                                    <div className="info-row mb-2">
                                        <div className="text-muted small d-flex align-items-center">
                                            Förfallodatum
                                            {/* R-PAY-1 — the deadline is when the money has
                                                ARRIVED, not when it was sent. A transfer posted
                                                on the due date is late, and that is the single
                                                most common way an on-time filer still pays a
                                                fee. Worth saying, not worth a paragraph under
                                                every due date. */}
                                            <InfoHint text={
                                                'Pengarna ska finnas på Skatteverkets konto senast '
                                                + 'den här dagen — betala i god tid, en överföring '
                                                + 'samma dag hinner oftast inte fram.'
                                            } size={13} />
                                        </div>
                                        <div className="fw-medium" style={{
                                            color: vatPeriod?.due_date && new Date(vatPeriod.due_date) < new Date() ? 'var(--bs-danger)' : undefined
                                        }}>
                                            {vatPeriod?.due_date ? moment(vatPeriod.due_date).format('D MMMM YYYY') : '—'}
                                        </div>
                                        {vatPeriod?.due_date_statutory
                                            && vatPeriod.due_date_statutory !== vatPeriod.due_date && (
                                            <div className="text-muted small">
                                                Flyttad från {moment(vatPeriod.due_date_statutory).format('D MMMM YYYY')},
                                                som inte är en vardag.
                                            </div>
                                        )}
                                    </div>

                                    {/* OCR is NOT computed here (G21, F-9).
                                        This used to build "16{orgnr}02" client-side.
                                        Skatteverket publishes no OCR algorithm — only
                                        a calculator — so the number was unverifiable,
                                        and a wrong payment reference is worse than no
                                        payment reference. */}
                                    <div className="info-row mb-2">
                                        <div className="text-muted small d-flex align-items-center">
                                            OCR-nummer
                                            {/* The explanation is worth having but crowded the
                                                row it explains, so it moves behind the icon. */}
                                            <InfoHint text={
                                                'OCR-numret är kopplat till ditt skattekonto. '
                                                + 'Skatteverket publicerar ingen formel för det, så '
                                                + 'LedgerLoop räknar inte fram något — använd '
                                                + 'e-tjänsten och spara numret, det är detsamma vid '
                                                + 'varje betalning.'
                                            } size={13} />
                                        </div>
                                        <div className="fw-medium">
                                            <a href={OCR_SERVICE_URL} target="_blank" rel="noopener noreferrer"
                                                className="d-inline-flex align-items-center gap-1">
                                                Räkna ut ditt OCR-nummer hos Skatteverket
                                                <ExternalLink size={13} />
                                            </a>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <Button
                            onClick={() => setShowDateModal(true)}
                            style={{ background: 'var(--primary)' }}
                            disabled={operationInProgress || isPreviewMode}
                            className="d-inline-flex align-items-center"
                            size="lg"
                        >
                            <CheckCircle size={18} className="me-2" />
                            Markera betalning som klar
                        </Button>
                    </>
                )}
            </div>

            {/* Payment Date Modal */}
            <Modal show={showDateModal} onHide={() => setShowDateModal(false)} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title>
                        <div className="d-flex align-items-center">
                            <Calendar size={20} className="me-2" style={{ color: 'var(--primary)' }} />
                            <span>Bekräfta betalning</span>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                    <div className="mb-3 p-3 rounded" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                        <div className="d-flex align-items-center mb-2">
                            <Info size={16} className="text-primary me-2" />
                            <small className="fw-medium">Betalningsinformation</small>
                        </div>
                        <div className="small text-muted">
                            <div>Belopp: <strong>{new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(declaredVat)}</strong></div>
                            <div>Bankgiro: <strong>5050-1055</strong></div>
                        </div>
                    </div>

                    <Form.Group>
                        <Form.Label className="fw-medium">När betalades momsen?</Form.Label>
                        <Form.Control
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="form-control-lg"
                        />
                        <Form.Text className="text-muted">
                            Välj det datum när betalningen skickades till Skatteverket
                        </Form.Text>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-0">
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowDateModal(false)}
                    >
                        Avbryt
                    </Button>
                    <Button
                        onClick={handleSubmitPaymentDate}
                        className="d-inline-flex align-items-center border-0"
                        style={{ background: 'var(--primary)' }}
                    >
                        <CheckCircle size={16} className="me-2" />
                        Bekräfta betalning
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default PaymentStep;
